import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "./contexts/AuthContext";
import { EstablishmentProvider } from "./contexts/EstablishmentContext";
import { CookieConsentProvider } from "./contexts/CookieConsentContext";
import CookieConsent from "./components/marketing/CookieConsent";
import { SentryErrorBoundary } from "./components/ErrorBoundary";

// Marketing pages
import MarketingIndex from "./pages/marketing/Index";
import Contact from "./pages/marketing/Contact";
import ThankYou from "./pages/marketing/ThankYou";
import Legal from "./pages/marketing/Legal";
import Lausanne from "./pages/marketing/Lausanne";
import Geneve from "./pages/marketing/Geneve";
import Fribourg from "./pages/marketing/Fribourg";
import Neuchatel from "./pages/marketing/Neuchatel";
import Sion from "./pages/marketing/Sion";
import PiktableVsMyLocalina from "./pages/marketing/comparatif/PiktableVsMyLocalina";
import PiktableVsForatable from "./pages/marketing/comparatif/PiktableVsForatable";
import PiktableVsAleno from "./pages/marketing/comparatif/PiktableVsAleno";
import PiktableVsTheFork from "./pages/marketing/comparatif/PiktableVsTheFork";
import PiktableVsOpenTable from "./pages/marketing/comparatif/PiktableVsOpenTable";
import PiktableVsZenchef from "./pages/marketing/comparatif/PiktableVsZenchef";
import MarketingNotFound from "./pages/marketing/NotFound";
import Blog from "./pages/marketing/Blog";
import BlogArticle from "./pages/marketing/BlogArticle";
import Pricing from "./pages/marketing/Pricing";
import OffreEcoleHoteliere from "./pages/marketing/OffreEcoleHoteliere";
import AlternativeGloriaFood from "./pages/marketing/AlternativeGloriaFood";

// Booking pages
import Book from "./pages/Book";
import BookEmbed from "./pages/BookEmbed";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import VerifyEmail from "./pages/VerifyEmail";
import SetPassword from "./pages/SetPassword";
import ForgotPassword from "./pages/ForgotPassword";
import Dashboard from "./pages/Dashboard";
import TableManagement from "./pages/TableManagement";
import Availability from "./pages/Availability";
import TimeSlots from "./pages/TimeSlots";
import ModifyReservation from "./pages/ModifyReservation";
import CancelReservation from "./pages/CancelReservation";
import UserManagement from "./pages/UserManagement";
import EmailLogs from "./pages/EmailLogs";
import Billing from "./pages/Billing";
import Notifications from "./pages/Notifications";
import Establishment from "./pages/Establishment";
import EmailSettings from "./pages/EmailSettings";
import Statistics from "./pages/Statistics";
import Help from "./pages/Help";
import Onboarding from "./pages/Onboarding";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminEstablishments from "./pages/admin/AdminEstablishments";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminEmailTemplates from "./pages/admin/AdminEmailTemplates";
import AdminFloorPlanRequests from "./pages/admin/AdminFloorPlanRequests";
import AdminEmailStats from "./pages/admin/AdminEmailStats";
import Unsubscribe from "./pages/Unsubscribe";
import AdminBilling from "./pages/admin/AdminBilling";
import AdminLeads from "./pages/admin/AdminLeads";
import AdminOnboardingPreview from "./pages/admin/AdminOnboardingPreview";
import AdminFeedbacks from "./pages/admin/AdminFeedbacks";
import AdminErrors from "./pages/admin/AdminErrors";
import AdminMarketing from "./pages/admin/AdminMarketing";
import ClientFollowupList from "./pages/admin/ClientFollowupList";
import ClientFollowupDetail from "./pages/admin/ClientFollowupDetail";
import AdminManagerNotifications from "./pages/admin/AdminManagerNotifications";
import SentryExamplePage from "./pages/SentryExamplePage";
import UnsubscribeLead from "./pages/UnsubscribeLead";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { RecoveryFlowHandler } from "./components/auth/RecoveryFlowHandler";
import { ScrollToTop } from "./components/ScrollToTop";

const queryClient = new QueryClient();

// Helper component to conditionally render CookieConsent based on route
const ConditionalCookieConsent = () => {
  const location = useLocation();
  
  // Hide popup on booking pages (/:slug and /:slug/embed)
  // These pages have their own inline cookie consent checkbox
  const isBookingPage = /^\/[^\/]+$/.test(location.pathname) || 
                        location.pathname.endsWith('/embed');
  
  if (isBookingPage) return null;
  
  return <CookieConsent />;
};

const App = () => (
  <SentryErrorBoundary>
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <CookieConsentProvider>
          <AuthProvider>
            <EstablishmentProvider>
              <TooltipProvider>
                <Toaster />
                <Sonner />
                <BrowserRouter>
                  <ScrollToTop />
                  <RecoveryFlowHandler>
                    <Routes>
                      {/* ============================================ */}
                      {/* MARKETING ROUTES (piktable.ch/...)          */}
                      {/* ============================================ */}

                      {/* Landing page */}
                      <Route path="/" element={<MarketingIndex />} />

                      {/* Pricing */}
                      <Route path="/pricing" element={<Pricing />} />

                      {/* Offre ciblée École Hôtelière (non liée depuis la landing) */}
                      <Route path="/offre/ecole-hoteliere" element={<OffreEcoleHoteliere />} />

                      {/* Campagne fin de vie GloriaFood (30.04.2027) */}
                      <Route path="/alternative-gloriafood" element={<AlternativeGloriaFood />} />

                      {/* City pages */}
                      <Route path="/lausanne" element={<Lausanne />} />
                      <Route path="/geneve" element={<Geneve />} />
                      <Route path="/fribourg" element={<Fribourg />} />
                      <Route path="/neuchatel" element={<Neuchatel />} />
                      <Route path="/sion" element={<Sion />} />

                      {/* Contact */}
                      <Route path="/contact" element={<Contact />} />
                      <Route path="/merci" element={<ThankYou />} />

                      {/* Legal */}
                      <Route path="/legal" element={<Legal />} />
                      
                      {/* Unsubscribe from emails */}
                      <Route path="/unsubscribe/:token" element={<Unsubscribe />} />
                      <Route path="/unsubscribe-lead/:token" element={<UnsubscribeLead />} />
                      
                      {/* Sentry test page */}
                      <Route path="/sentry-example-page" element={<SentryExamplePage />} />

                      {/* Blog */}
                      <Route path="/blog" element={<Blog />} />
                      <Route path="/blog/:slug" element={<BlogArticle />} />

                      {/* Comparison pages */}
                      <Route path="/comparatif/piktable-vs-mylocalina" element={<PiktableVsMyLocalina />} />
                      <Route path="/comparatif/piktable-vs-foratable" element={<PiktableVsForatable />} />
                      <Route path="/comparatif/piktable-vs-aleno" element={<PiktableVsAleno />} />
                      <Route path="/comparatif/piktable-vs-thefork" element={<PiktableVsTheFork />} />
                      <Route path="/comparatif/piktable-vs-opentable" element={<PiktableVsOpenTable />} />
                      <Route path="/comparatif/piktable-vs-zenchef" element={<PiktableVsZenchef />} />

                      {/* ============================================ */}
                      {/* BOOKING ROUTES (piktable.ch/:slug/...)      */}
                      {/* ============================================ */}

                      {/* Public booking pages with slug */}
                      <Route path="/:slug/embed" element={<BookEmbed />} />
                      <Route path="/:slug/modify-reservation/:token" element={<ModifyReservation />} />
                      <Route path="/:slug/cancel-reservation/:token" element={<CancelReservation />} />

                      {/* Auth routes with slug */}
                      <Route path="/:slug/login" element={<Login />} />
                      <Route path="/:slug/signup" element={<Signup />} />
                      <Route path="/:slug/verify-email" element={<VerifyEmail />} />
                      <Route path="/:slug/set-password" element={<SetPassword />} />
                      <Route path="/:slug/forgot-password" element={<ForgotPassword />} />
                      <Route path="/forgot-password" element={<ForgotPassword />} />

                      {/* Onboarding with slug */}
                      <Route path="/:slug/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />

                      {/* Protected Dashboard with slug */}
                      <Route path="/:slug/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                      <Route path="/:slug/dashboard/tables" element={<ProtectedRoute><TableManagement /></ProtectedRoute>} />
                      <Route path="/:slug/dashboard/availability" element={<ProtectedRoute><Availability /></ProtectedRoute>} />
                      <Route path="/:slug/dashboard/time-slots" element={<ProtectedRoute><TimeSlots /></ProtectedRoute>} />
                      <Route path="/:slug/dashboard/users" element={<ProtectedRoute requireAdmin><UserManagement /></ProtectedRoute>} />
                      <Route path="/:slug/dashboard/emails" element={<ProtectedRoute requireAdmin><EmailLogs /></ProtectedRoute>} />
                      <Route path="/:slug/dashboard/billing" element={<ProtectedRoute requireAdmin><Billing /></ProtectedRoute>} />
                      <Route path="/:slug/dashboard/notifications" element={<ProtectedRoute requireAdmin><Notifications /></ProtectedRoute>} />
                      <Route path="/:slug/dashboard/establishment" element={<ProtectedRoute requireAdmin><Establishment /></ProtectedRoute>} />
                      <Route path="/:slug/dashboard/email-settings" element={<ProtectedRoute requireAdmin><EmailSettings /></ProtectedRoute>} />
                      <Route path="/:slug/dashboard/statistics" element={<ProtectedRoute requireAdmin><Statistics /></ProtectedRoute>} />
                      <Route path="/:slug/dashboard/help" element={<ProtectedRoute><Help /></ProtectedRoute>} />

                      {/* ============================================ */}
                      {/* ADMIN ROUTES (no slug needed)               */}
                      {/* ============================================ */}
                      <Route path="/admin" element={<ProtectedRoute requireSuperAdmin><AdminDashboard /></ProtectedRoute>} />
                      <Route path="/admin/establishments" element={<ProtectedRoute requireSuperAdmin><AdminEstablishments /></ProtectedRoute>} />
                      <Route path="/admin/users" element={<ProtectedRoute requireSuperAdmin><AdminUsers /></ProtectedRoute>} />
                      <Route path="/admin/email-templates" element={<ProtectedRoute requireSuperAdmin><AdminEmailTemplates /></ProtectedRoute>} />
                      <Route path="/admin/floor-plan-requests" element={<ProtectedRoute requireSuperAdmin><AdminFloorPlanRequests /></ProtectedRoute>} />
                      <Route path="/admin/billing" element={<ProtectedRoute requireSuperAdmin><AdminBilling /></ProtectedRoute>} />
                      <Route path="/admin/emails" element={<ProtectedRoute requireSuperAdmin><AdminEmailStats /></ProtectedRoute>} />
                      <Route path="/admin/leads" element={<ProtectedRoute requireSuperAdmin><AdminLeads /></ProtectedRoute>} />
                      <Route path="/admin/feedbacks" element={<ProtectedRoute requireSuperAdmin><AdminFeedbacks /></ProtectedRoute>} />
                      <Route path="/admin/errors" element={<ProtectedRoute requireSuperAdmin><AdminErrors /></ProtectedRoute>} />
                      <Route path="/admin/onboarding-preview" element={<ProtectedRoute requireSuperAdmin><AdminOnboardingPreview /></ProtectedRoute>} />
                      <Route path="/admin/instagram" element={<Navigate to="/admin/marketing?project=piktable" replace />} />
                      <Route path="/admin/marketing" element={<ProtectedRoute requireSuperAdmin><AdminMarketing /></ProtectedRoute>} />
                      <Route path="/admin/client-followup" element={<ProtectedRoute requireSuperAdmin><ClientFollowupList /></ProtectedRoute>} />
                      <Route path="/admin/client-followup/:establishmentId" element={<ProtectedRoute requireSuperAdmin><ClientFollowupDetail /></ProtectedRoute>} />
                      <Route path="/admin/manager-notifications" element={<ProtectedRoute requireSuperAdmin><AdminManagerNotifications /></ProtectedRoute>} />
                      {/* ============================================ */}
                      {/* LEGACY ROUTES (redirect support)            */}
                      {/* ============================================ */}
                      <Route path="/login" element={<Login />} />
                      <Route path="/signup" element={<Signup />} />
                      <Route path="/verify-email" element={<VerifyEmail />} />
                      <Route path="/set-password" element={<SetPassword />} />
                      <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
                      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                      <Route path="/dashboard/tables" element={<ProtectedRoute><TableManagement /></ProtectedRoute>} />
                      <Route path="/dashboard/availability" element={<ProtectedRoute><Availability /></ProtectedRoute>} />
                      <Route path="/dashboard/time-slots" element={<ProtectedRoute><TimeSlots /></ProtectedRoute>} />
                      <Route path="/dashboard/users" element={<ProtectedRoute requireAdmin><UserManagement /></ProtectedRoute>} />
                      <Route path="/dashboard/emails" element={<ProtectedRoute requireAdmin><EmailLogs /></ProtectedRoute>} />
                      <Route path="/dashboard/billing" element={<ProtectedRoute requireAdmin><Billing /></ProtectedRoute>} />
                      <Route path="/dashboard/notifications" element={<ProtectedRoute requireAdmin><Notifications /></ProtectedRoute>} />
                      <Route path="/dashboard/establishment" element={<ProtectedRoute requireAdmin><Establishment /></ProtectedRoute>} />
                      <Route path="/dashboard/email-settings" element={<ProtectedRoute requireAdmin><EmailSettings /></ProtectedRoute>} />
                      <Route path="/dashboard/statistics" element={<ProtectedRoute requireAdmin><Statistics /></ProtectedRoute>} />
                      <Route path="/dashboard/help" element={<ProtectedRoute><Help /></ProtectedRoute>} />

                      {/* ============================================ */}
                      {/* ESTABLISHMENT BOOKING (/:slug)               */}
                      {/* Catch-all for establishment slugs           */}
                      {/* Must be LAST before 404 to allow marketing  */}
                      {/* routes to take precedence                   */}
                      {/* ============================================ */}
                      <Route path="/:slug" element={<Book />} />

                      {/* 404 - Marketing style for root level */}
                      <Route path="*" element={<MarketingNotFound />} />
                    </Routes>
                  </RecoveryFlowHandler>
                  <ConditionalCookieConsent />
                </BrowserRouter>
              </TooltipProvider>
            </EstablishmentProvider>
          </AuthProvider>
        </CookieConsentProvider>
      </QueryClientProvider>
    </HelmetProvider>
  </SentryErrorBoundary>
);

export default App;
